import { api, TCustomAxiosPaginationResponse, TCustomAxiosResponse } from '..';
import { TUser, TUserFilterOption } from '@/types/User.types';
import { getBeUserFieldNameFromFilterOption } from '@/utils/stringUtils';
import { ITEMS_PER_PAGE } from '@/theme/components/table';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import { TNotificationChannel } from '@/types/Notification.types';
import { apiPaths } from '@/services/api/paths';
import { bareApi } from '../bareApi';

export const userGet = async (): Promise<TUser> => {
  const { data } = await api.get(apiPaths.user.root);
  const user = data.data as TUser;

  return user;
};

/**
 * The same request as userGet, issued through the interceptor-free client.
 *
 * The boot-time session check owns its own refresh-and-retry, and states plainly
 * what each failure means: 4xx on the refresh ends the session, a network error
 * or 5xx is retryable and must never be destructive. Going through `api` would
 * let the interceptor run its own refresh underneath the boot gate and tear the
 * session down while the check was still awaiting.
 *
 * Only the *user* fetch needs this. The secondary boot fetches stay on `api`,
 * where refresh-and-replay is what you want, and use skipSessionTeardown so they
 * can degrade without ending the session.
 */
export const userGetForBoot = async (): Promise<TUser> => {
  const { data } = await bareApi.get(apiPaths.user.root);

  return data.data as TUser;
};

export const getAllActivatedUsers = async (
  page: number,
  filterOption: TUserFilterOption,
  filterText: string,
): Promise<TCustomAxiosPaginationResponse<TUser[]>> => {
  const filterTypeBE = filterOption ? getBeUserFieldNameFromFilterOption(filterOption) : '';

  const params: any = {
    limit: ITEMS_PER_PAGE,
    page,
  };

  if (filterText && filterText?.length >= MIN_CHARS_TO_DO_SEARCH) {
    params[filterTypeBE] = filterText;
  }

  const { data } = await api.get(apiPaths.user.active, {
    params,
  });

  return data;
};

export const getAllDeactivatedUsers = async (
  page: number,
  filterOption: TUserFilterOption,
  filterText: string,
): Promise<TCustomAxiosPaginationResponse<TUser[]>> => {
  const filterTypeBE = filterOption ? getBeUserFieldNameFromFilterOption(filterOption) : '';

  const params: any = {
    limit: ITEMS_PER_PAGE,
    page,
  };

  if (filterText && filterText?.length >= MIN_CHARS_TO_DO_SEARCH) {
    params[filterTypeBE] = filterText;
  }

  const { data } = await api.get(apiPaths.user.deactivated, {
    params,
  });

  return data;
};

export const activateUserPost = async (userId: string) => {
  const data = await api.post(apiPaths.user.reactivateOrgUser(userId));
  return data;
};

export const deactivateUserPost = async (userId: string) => {
  const data = await api.delete(apiPaths.user.byId(userId));
  return data;
};

export const signInPost = async (email: string, password: string) => {
  const response = await api.post(apiPaths.user.signIn, {
    email,
    password,
  });

  return response.data.data;
};

// bareApi, not api: the interceptor's refresh exclusion only covers '/login', and
// '/v1/user/logout' does not match it — so a 401 here would trigger a token
// refresh for a user who is already on their way out, and on refresh failure
// re-enter endSession. Fire-and-forget only works if the call cannot start a
// second flow.
export const signOutPost = async (): Promise<TUser> => {
  const res = await bareApi.post(apiPaths.user.signOut);

  return res.data.data;
};

export const forgotPasswordPost = async (email: string) => {
  await api.post(apiPaths.user.forgotPassword, {
    email,
  });

  return 'ok';
};

export const verifyAuthToken = async (body: { token: string; email: string }): Promise<{ success: boolean }> => {
  const { data } = await api.post(apiPaths.user.verifyAuthToken, body);
  return data.data;
};

export const setInitialUserPassword = async (body: {
  email: string;
  token: string;
  newPassword: string;
}): Promise<{ success: boolean }> => {
  // No try/finally here. A `return` inside `finally` swallows the exception and
  // overrides the try's value, so every failed password setup reported success:
  // the UI toasted "Password change successful", redirected to sign-in, and the
  // user was left with no password and no idea why they could not log in.
  const { data } = await api.post(apiPaths.user.setInitialPassword, body);

  return data.data;
};

export const userNotificationPreferencesGet = async (): Promise<TNotificationChannel[]> => {
  const { data } = await api.get<{ channels: TNotificationChannel[] }>(apiPaths.user.notificationPreferences);

  return data.channels;
};

export const updateUserNotificationPreferencesPatch = async (
  newNotificationPreferences: TNotificationChannel[],
): Promise<string> => {
  const { data } = await api.patch<TCustomAxiosResponse<string>>(apiPaths.user.notificationPreferences, {
    channels: newNotificationPreferences,
  });

  return data.data;
};

export const promoteUserToOrganizationAdmin = async (
  userId: string,
): Promise<{
  success: boolean;
  message: string;
  userId: string;
  newRole: string;
  organizationId: string;
}> => {
  const { data } = await api.post<
    TCustomAxiosResponse<{
      success: boolean;
      message: string;
      userId: string;
      newRole: string;
      organizationId: string;
    }>
  >(apiPaths.user.promote(userId));
  return data.data;
};

export const reassignDeactivatedUserToDifferentOrganization = async (
  userId: string,
  organizationId: string,
): Promise<{
  success: boolean;
  message: string;
  userId: string;
  organizationId: string;
}> => {
  const { data } = await api.patch<
    TCustomAxiosResponse<{
      success: boolean;
      message: string;
      userId: string;
      organizationId: string;
    }>
  >(apiPaths.user.reassign(userId), {
    newOrganizationId: organizationId,
  });
  return data.data;
};
